import { TokenWithValue } from '@/domain/common/types'
import { assets } from '@/ui/assets'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { PageLayout } from '@/ui/layouts/PageLayout'
import { useConfettiContext } from '@/ui/molecules/confetti/Confetti'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'
import { useEffect } from 'react'
import { SwapMode } from '@/features/bridge/logic/useBridge'
import { BridgeObjective } from '@/features/actions/flavours/bridge/types'
export interface SuccessViewProps {
  runConfetti: boolean
  fromToken: TokenWithValue
  swapMode: SwapMode
  resetForm: () => void
  actions: BridgeObjective[]
  isModal?: boolean
}

export function SuccessView({ fromToken, runConfetti, resetForm, swapMode, isModal }: SuccessViewProps) {
  const { runAnimation } = useConfettiContext()

  useEffect(() => {
    if (runConfetti) {
      runAnimation()
    }
  }, [runConfetti, runAnimation])

  return (
    <PageLayout className={cn('pt-8', isModal ? 'mt-0' : 'xl:pt-28')}>
      <div className="flex flex-col items-center justify-center">
        <img src={assets.success} alt="success-img" className={cn(isModal ? 'w-16' : 'w-24')} />
        <Typography variant="h1" className={cn('text-center', isModal ? 'mt-4 text-4xl lg:text-4xl' : 'mt-8 ')}>
          Congrats, all done!
        </Typography>

        <Panel className="mt-8 min-w-full max-w-3xl">
          <Panel.Header>
            <Panel.Title className="text-xl">Summary</Panel.Title>
          </Panel.Header>

          <Panel.Content>
            <div className={cn('mt-4 flex flex-col gap-9', 'md:flex-row')}>
              <div className="flex grow flex-col" data-testid={testIds.easyBorrow.success.deposited}>
                <Typography variant="prompt" className="mb-2 font-semibold text-primary">
                  Transferred
                </Typography>

                <Item key={fromToken.token.symbol} {...fromToken} isL1={swapMode === 'bridgeToHyperEVM'} />
              </div>

              <div className="flex grow flex-col" data-testid={testIds.easyBorrow.success.borrowed}>
                <Typography variant="prompt" className="mb-2 font-semibold text-primary">
                  Received
                </Typography>

                <Item key={fromToken.token.symbol} {...fromToken} isL1={swapMode === 'bridgeToHyperCore'} />
              </div>
            </div>

            <div className="mt-8 flex gap-4">
              <Button className="flex-1 bg-white/10 text-primary" onClick={resetForm} rounded="full">
                Close
              </Button>
            </div>
          </Panel.Content>
        </Panel>
      </div>
    </PageLayout>
  )
}

function Item({ token, value, approx = false, isL1 = false }: TokenWithValue & { approx?: boolean; isL1?: boolean }) {
  return (
    <div className="flex items-center border-t">
      <div className="relative">
        <TokenIcon token={token} className="h-6 w-6" />
        {isL1 && (
          <div className="-translate-y-1/2 absolute top-0 right-0 flex h-4 w-4 translate-x-1/2 items-center justify-center rounded border border-white/30 bg-black/90 text-[8px]">
            L1
          </div>
        )}
      </div>

      <Typography className="ml-2 font-primary">{token.symbol}</Typography>

      <div className="flex grow flex-col py-3 pr-4">
        <Typography className="text-right font-primary">
          {approx && <span className="mr-1">~</span>}
          {token.format(value, { style: 'auto' })}
        </Typography>
        <Typography variant="prompt" className="text-right">
          {approx && <span className="mr-1">~</span>}
          {token.formatUSD(value)}
        </Typography>
      </div>
    </div>
  )
}
